import { motion } from "framer-motion";
import PageHero from "@/components/layout/PageHero";
import Team from "@/components/sections/Team";
import CTA from "@/components/sections/CTA";
import { IMAGES } from "@/lib/constants";

const PRINCIPLES = [
    {
        n: "01",
        title: "Risk, not reporting.",
        body: "We do not build another ESG dashboard. We convert emissions into the language of credit committees, procurement heads, and CFOs — because that is where the money moves."
    },
    {
        n: "02",
        title: "MSME-first, always.",
        body: "70% of India's exports run through MSMEs. Our defaults, our data models, and our pricing are all designed for that reality — not for Fortune 500 sustainability teams."
    },
    {
        n: "03",
        title: "Defensible over decorative.",
        body: "Every number Carbonis produces has to survive an auditor, a regulator, and a credit committee. That constraint sits at the center of our engineering."
    }
];

export default function WhoWeAre() {
    return (
        <div data-testid="page-who-we-are">
            <PageHero
                title={<>A company built for the moment climate <em className="text-gold not-italic font-light">becomes a P&amp;L line.</em></>}
                lead="Carbonis is a climate risk intelligence venture from UP/OR/OUT, founded in 2024 and headquartered in Hyderabad. We build the operating layer that turns carbon data into decisions — for banks, regulators, and the MSMEs that anchor emerging-market supply chains."
                image={IMAGES.boardroom}
            />

            <section className="relative py-24 lg:py-36 border-t border-navy/10">
                <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                        <div className="lg:col-span-5">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-px bg-gold"></div>
                                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">Principles</span>
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                                Three commitments <em className="text-gold not-italic font-light">that shape the product.</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 lg:self-end">
                            <p className="text-navy/60 text-lg leading-relaxed">
                                We do not treat climate as a compliance vertical. We treat it as an emerging class of financial risk — and design accordingly.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-16 lg:gap-24">
                        {PRINCIPLES.map((p) => (
                            <motion.div
                                key={p.n}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.9 }}
                                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                                data-testid={`principle-${p.n}`}
                            >
                                <div className="lg:col-span-4">
                                    <div className="chapter-num text-[120px] md:text-[180px] text-gold/90">{p.n}</div>
                                </div>
                                <div className="lg:col-span-8 lg:pt-8">
                                    <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-navy">{p.title}</h3>
                                    <p className="mt-6 text-navy/60 text-base lg:text-lg leading-relaxed max-w-[600px]">{p.body}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <Team />

            <CTA />
        </div>
    );
}
